import { AfterViewInit, DestroyRef, Directive, ElementRef, EventEmitter, inject, input, Output } from '@angular/core';
import { distinctUntilChanged, map } from 'rxjs/operators';

import { isEqualSimpleObject } from '@celum/core/util';

import { ResizeObserverHelper } from './resize-observer-helper';

/**
 * A directive which registers its element with a resizeObserver and adds an according overlay class in case of the element having some overlay applied.
 * There is only one instance of a resize observer that manages multiple elements.
 */
@Directive({ selector: '[hasOverflow]' })
export class HasOverflowDirective implements AfterViewInit {
  /** Specify the debounce time of the resize events as there will be a lot of events during manual resizing or animations. */
  public resizeDebounce = input(100);
  /** Specify which overflow directions to listen to. Default is listen only to vertical overflow (aka `y`). */
  public overflowDirection = input<'xy' | 'x' | 'y'>('y');
  /** Optionally specify a different class name prefix to use. Default is `has-overflow`, which will result in `has-overflow-x` and `has-overflow-y` classes to be added. */
  public classNamePrefix = input('has-overflow');

  /** Listen to changes of the "has overflow" state. Based on the setting of {@link overflowDirection}, you will get the `y` and/or `x` value. */
  @Output() public readonly overflowChanged = new EventEmitter<{ x?: boolean; y?: boolean }>();

  private resizeObserverHelper: ResizeObserverHelper;
  private destroyRef = inject(DestroyRef);
  private element = inject(ElementRef);

  public ngAfterViewInit(): void {
    this.resizeObserverHelper = new ResizeObserverHelper(this.element.nativeElement, this.resizeDebounce(), this.destroyRef);
    this.resizeObserverHelper
      .startListen()
      .pipe(
        map(entry => {
          const el = entry.target;
          const classesToAdd: string[] = [];
          const classesToRemove: string[] = [];
          const xClass = this.classNamePrefix() + '-x';
          const yClass = this.classNamePrefix() + '-y';

          let hasOverflowY: boolean;
          let hasOverflowX: boolean;

          if (this.overflowDirection() === 'x' || this.overflowDirection() === 'xy') {
            hasOverflowX = HasOverflowDirective.checkForOverflow(el.clientWidth, el.scrollWidth, xClass, classesToAdd, classesToRemove);
          }

          if (this.overflowDirection() === 'y' || this.overflowDirection() === 'xy') {
            hasOverflowY = HasOverflowDirective.checkForOverflow(el.clientHeight, el.scrollHeight, yClass, classesToAdd, classesToRemove);
          }

          classesToAdd.length > 0 && this.element.nativeElement.classList.add(...classesToAdd);
          classesToRemove.length > 0 && this.element.nativeElement.classList.remove(...classesToRemove);

          return { x: hasOverflowX, y: hasOverflowY };
        }),
        distinctUntilChanged(isEqualSimpleObject)
      )
      .subscribe(changes => this.overflowChanged.emit(changes));
  }

  /**
   * Trigger a check. This can be handy if you run into issues e.g. with Safari on iOS where the ResizeObserver does
   * not emit reliably in every case (e.g. autosize textarea size changes are not recognized).
   */
  public triggerCheck(): void {
    this.resizeObserverHelper.triggerCheck();
  }

  private static checkForOverflow(size: number, scrollSize: number, className: string, classesToAdd: string[], classesToRemove: string[]): boolean {
    const hasOverflow = size < scrollSize;
    if (hasOverflow) {
      classesToAdd.push(className);
      return true;
    } else {
      classesToRemove.push(className);
      return false;
    }
  }
}
